import { useEffect, useState } from "react";
import "./details.scss";
import { InputField } from "../../../../components/controls/InputField";
import ButtonControl from "../../../../components/controls/ButtonControl";
import _ from "lodash";
import {
  CheckboxControl,
  DatePickerControl,
  SelectListControl,
} from "../../../../components/controls";
import { useValidationManager } from "../../../../hooks/useValidation";
import Loading from "../../../../components/Loading";
import CircularProgress from "@mui/material/CircularProgress";
import { DialogControl } from "../../../../components/controls/DialogControl";
import { v4 as uuidv4 } from "uuid";
import { useOrgManager, useAppUserManager } from "../../../../hooks/useManagers";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InputFieldTreePicker from "./InputFieldTreePicker";
import {
  FormatUTCDateTimeM,
  FormatUTCOnSave,
} from "../../../../utilities/DateFormater";

const GeneralSurveyDetails = ({
  params,
  authMgr,
  canWrite,
  generalSurveyConfigMgr,
  survey,
  onCancel,
  onSave,
  onErrors,
  onValidationErrors,
}) => {
  const [surveyItem, setSurveyItem] = useState(
    survey ?? generalSurveyConfigMgr?.DefaultItem
  );
  const [saving, setSaving] = useState(false);
  const [forceValidate, setForceValidate] = useState(false);
  const [formattedSurveyTemplates, setFormattedSurveyTemplates] =
    useState(null);
  const [userData, setUserData] = useState(null);
  const [orgTree, setOrgTree] = useState(null);
  const [orgTreeData, setOrgTreeData] = useState(null);
  const [openUserDialogue, setOpenUserDialogue] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingOrgTree, setIsLoadingOrgTree] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [defaultSelectedIds, setDefaultSelectedIds] = useState(null);
  const validationMgr = useValidationManager(false, "GENERALSURVEY");
  const orgMgr = useOrgManager();
  const appUserMgr = useAppUserManager();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleOnCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  useEffect(() => {
    if (generalSurveyConfigMgr && orgMgr) {
      generalSurveyConfigMgr?.SurveyTemplateFormatted.then((r) => {
        setFormattedSurveyTemplates(r);
      });
      if (surveyItem.UserGeneralSurveys) {
        setDefaultSelectedIds(
          _.map(surveyItem.UserGeneralSurveys, (user) => user.UserId)
        );
      }
    }
  }, [generalSurveyConfigMgr, orgMgr, params]);

  useEffect(() => {
    if (!startDate) {
      if (surveyItem && !surveyItem?.StartDate) {
        setStartDate(new Date());
      } else if (surveyItem && surveyItem?.StartDate) {
        setStartDate(FormatUTCDateTimeM(surveyItem?.StartDate));
      }
    }
  }, [surveyItem?.StartDate]);

  useEffect(() => {
    if (!endDate) {
      if (surveyItem && surveyItem?.EndDate) {
        setEndDate(FormatUTCDateTimeM(surveyItem?.EndDate));
      }
    }
  }, [surveyItem?.EndDate]);

  useEffect(() => {
    const allP = [];
    if (userData == null) {
      console.log("Loading users for general survey details");
      setIsLoadingUsers(true);
      allP.push(
        appUserMgr?.GetShareTree().then((r) => {
          if (r.Success && r?.Items?.length > 0) {
            console.log("Users loaded");
            return r.Items;
          } else {
            console.log("Users NOT loaded");
            return null;
          }
        })
      );
    }

    Promise.all(allP).then((r) => {
      if (r[0]) {
        setUserData(r[0]);
      }
      setIsLoadingUsers(false);
    });
  }, [appUserMgr]);

  useEffect(() => {
    if (orgMgr) {
      loadOrgTree(orgMgr).then(() => {
        setIsLoadingOrgTree(false);
      });
    }
  }, [orgMgr]);

  const loadOrgTree = async (orgMgr) => {
    let result = await orgMgr.loadOrgUserTree(null, true).then((r) => {
      setOrgTreeData(r);
      return orgMgr.UsersOnly();
    });
    setOrgTree(result);
    getInitialUserList(result);
  }

  const getInitialUserList = async (myOrgTree) => {
    const formattedIds = surveyItem.UserGeneralSurveys.map(u => u.UserId);

    // dedupe usersToAdd
    const usersToAdd = Array.from(
                        new Map(
                          myOrgTree
                            .filter(user => formattedIds.includes(user.UserId))
                            .map(user => [user.UserId, user])
                        ).values()
                      );

    setSelectedUsers(usersToAdd);
  };

  const handleOnSave = async (e) => {
    const validateResult = validationMgr.checkIsGroupValid(surveyItem);
    if (validateResult?.length === 0) {
      setForceValidate(false);
      if (onSave) {
        let result;
        let updateUserResult;
        setSaving(true);
        surveyItem.StartDate = FormatUTCOnSave(startDate);
        surveyItem.EndDate = endDate !== null ? FormatUTCOnSave(endDate) : null;
        if (e === "NewSurvey") {
          result = await onSave(surveyItem, true);
        } else {
          result = await onSave(surveyItem);
          if (surveyItem.Id) {
            updateUserResult = await generalSurveyConfigMgr.UpdateUsers(
              surveyItem.UserGeneralSurveys
            );
          }
        }

        if (result.Success) {
          if (updateUserResult) {
            handleOnCancel();
          } else if (e === "NewSurvey") {
            setOpenUserDialogue(true);
            handleOnChange(result?.Items.first()?.Id, "Id");
          } else handleOnCancel();
        } else if (onErrors) {
          onErrors([
            result?.Message?.DisplayMessage ?? [
              ...(result?.MessageDetails ?? ["Unknown Error"]),
            ],
          ]);
        }

        setSaving(false);
        return result;
      }
    } else if (onErrors) {
      setForceValidate(true);
      onValidationErrors(validateResult);
    }
  };

  const handleOnChange = (value, field) => {
    let updatedSurvey = { ...surveyItem };

    if (field === "DELETE") {
      const removedValueIndex = _.findIndex(
        updatedSurvey.UserGeneralSurveys,
        (v) => v.UserId === value
      );
      const removedValue = _.find(
        updatedSurvey.UserGeneralSurveys,
        (v) => v.UserId === value
      );
      setSelectedUsers(
        _.map(
          _.filter(selectedUsers, (user) => {
            if (user !== value && !removedValue?.IsDeleted) {
              return user;
            }
          })
        )
      );
      if (removedValue?.IsDeleted === undefined) {
        updatedSurvey.UserGeneralSurveys?.splice(removedValueIndex, 1);
      } else {
        removedValue["IsDeleted"] = true;
        updatedSurvey.UserGeneralSurveys?.splice(
          removedValueIndex,
          1,
          removedValue
        );
      }
      const usersSelected = [...selectedUsers];
      _.remove(usersSelected, (user) => user === value);
      setSelectedUsers(usersSelected);
    } else if (field === "UserGeneralSurveys") {
      const formattedUsers = value.reduce((users, v) => {
        const userExist = _.find(
          updatedSurvey.UserGeneralSurveys,
          (d) => v === d.UserId
        );
        if (userExist) {
          const removedValueIndex = _.findIndex(
            updatedSurvey.UserGeneralSurveys,
            (x) => x.UserId === v
          );
          userExist["IsDeleted"] = false;
          updatedSurvey.UserGeneralSurveys?.splice(
            removedValueIndex,
            1,
            userExist
          );
        } else
          users.push({
            UserId: v,
            GeneralSurveyId: surveyItem?.Id,
            Id:
              _.find(
                updatedSurvey.UserGeneralSurveys,
                (user) => user.Id === value
              ) ?? uuidv4(),
            SurveyStartDate: surveyItem?.StartDate,
          });
        return users;
      }, []);
      updatedSurvey[field] = updatedSurvey[field]?.concat(formattedUsers);

      // set selected users here
      const formattedIds = formattedUsers.map(u => u.UserId);

      // dedupe usersToAdd
      const usersToAdd = Array.from(
                          new Map(
                            orgTree
                              .filter(user => formattedIds.includes(user.UserId))
                              .map(user => [user.UserId, user])
                          ).values()
                        );

      // dedupe newSelectedUsers
      const newSelectedUsers = Array.from(
                                new Map(
                                  [...usersToAdd, ...selectedUsers].map(user => [user.UserId, user])
                                ).values()
                              );
      setSelectedUsers(newSelectedUsers);
    } else {
      if (field === "StartDate") {
        setStartDate(value);
      } else if (field === "EndDate") {
        setEndDate(value);
      } else {
        updatedSurvey[field] = value;
      }
    }
    setSurveyItem(updatedSurvey);
  };

  return (
    <div className="control-box-wrapper">
      {surveyItem && (
        <>
          <DialogControl
            openDialog={openUserDialogue}
            title={"Add Users"}
            disableOk={selectedUserIds?.length === 0}
            onCancel={() => {
              setOpenUserDialogue(false);
              setSelectedUserIds([]);
            }}
            onOk={() => {
              setOpenUserDialogue(false);
              handleOnChange(selectedUserIds, "UserGeneralSurveys");
            }}
            okText={"add"}
          >
                {!(isLoadingUsers || isLoadingOrgTree) ? (
                  <div
                    className="d-flex align-items-center mt-4"
                    style={{ height: 40 }}
                  >
                    {userData && defaultSelectedIds ? (
                      <InputFieldTreePicker
                        setSelectedUserIds={setSelectedUserIds}
                        selectedUserIds={selectedUserIds}
                        appUserMgr={appUserMgr}
                        userData={userData}
                      />
                    ) : (
                      <div>
                        {!userData ? (
                          <div>No users found for the your organization.</div>
                        ) : (
                          <div>Unknown Error while loading organizations</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="share-dialog-loader">
                    <CircularProgress color={"inherit"} size={25} />
                  </div>
                )}
          </DialogControl>
          <div className="control-box box-two-column">
            <InputField
              forceValidate={forceValidate}
              type="text"
              value={surveyItem?.Name}
              title="Title"
              fieldName="Name"
              groupId={"GENERALSURVEY"}
              onChange={handleOnChange}
            />
            <InputField
              forceValidate={forceValidate}
              type="textarea"
              maxLength={500}
              value={surveyItem?.Description}
              title="Survey Instructions"
              fieldName="Description"
              groupId={"GENERALSURVEY"}
              onChange={handleOnChange}
            />
            {generalSurveyConfigMgr && formattedSurveyTemplates ? (
              <InputField
                forceValidate={forceValidate}
                value={surveyItem?.SurveyTemplateId}
                title="Template Name"
                fieldName="SurveyTemplateId"
                groupId={"GENERALSURVEY"}
                onChange={handleOnChange}
              >
                <SelectListControl
                  textValuePairs={formattedSurveyTemplates ?? []}
                />
              </InputField>
            ) : (
              <Loading />
            )}
            {startDate && (
              <InputField
                title="Start Date"
                forceValidate={forceValidate}
                value={startDate}
                maxLength={100}
                onChange={handleOnChange}
                groupId={"GENERALSURVEY"}
                fieldName="StartDate"
              >
                <DatePickerControl includeTime={true} />
              </InputField>
            )}
            <InputField
              title="End Date"
              forceValidate={forceValidate}
              value={endDate}
              maxLength={100}
              onChange={handleOnChange}
              groupId={"GENERALSURVEY"}
              fieldName="EndDate"
            >
              <DatePickerControl includeTime={true} />
            </InputField>
            <InputField
              title="Active?"
              value={
                surveyItem?.IsActive === undefined ||
                surveyItem?.IsActive === null
                  ? true
                  : surveyItem?.IsActive
              }
              fieldName="IsActive"
              groupId={"GENERALSURVEY"}
              onChange={handleOnChange}
            >
              <CheckboxControl />
            </InputField>
          </div>
          <div className="control-box box-two-column role-perm-editor__wrapper">
            <div className="role-perm-editor__header">
              <h3>Users</h3>
              <ButtonControl
                disabled={
                  !surveyItem.Name ||
                  !surveyItem.StartDate ||
                  !surveyItem.Description ||
                  !surveyItem.SurveyTemplateId
                }
                onClick={() =>
                  surveyItem.Id
                    ? setOpenUserDialogue(true)
                    : handleOnSave("NewSurvey")
                }
              >
                Add Users
              </ButtonControl>
            </div>
            <div className="role-perm-editor__search">
              <InputField
                type="text"
                value={filterInput}
                fieldName="search"
                disableError={true}
                placeholder="Search User Name"
                onChange={(value) => setFilterInput(value?.toLowerCase())}
              ></InputField>
            </div>
            <div className="detail-table-wrapper general-survey-table">
              <table className="__table">
                <thead className="__head">
                  <tr>
                    <th className="__name">First Name</th>
                    <th className="__flex">Last Name</th>
                    <th className="__flex">Organization</th>
                  </tr>
                </thead>
                <tbody>
                  {orgMgr && generalSurveyConfigMgr ? (
                    _.map(
                      _.filter(
                        selectedUsers,
                        (user) =>
                          user?.Name.replace(",", "")
                            .toLowerCase()
                            .includes(filterInput) &&
                          _.map(
                            _.filter(
                              surveyItem.UserGeneralSurveys,
                              (userSurvey) => {
                                if (!userSurvey.IsDeleted) {
                                  return userSurvey;
                                }
                              }
                            ),
                            (user) => `u-${user.UserId}`
                          ).includes(user.Id)
                      ),
                      (users, i) => {
                        const org = _.find(
                          orgTreeData,
                          (o) => o.OrgId === users.OrgId
                        )?.Name;
                        return (
                          <tr key={i}>
                            <td className="__name">
                              {users.FullName.split(",")[1]}
                            </td>
                            <td className="__flex">
                              {users.FullName.split(",")[0]}
                            </td>
                            <td className="__flex">{org}</td>
                            <td className="__icon">
                              {
                                <FontAwesomeIcon
                                  className={"icon btn-icon-fa"}
                                  icon={faTrashCan}
                                  onClick={() =>
                                    handleOnChange(users.UserId, "DELETE")
                                  }
                                />
                              }
                            </td>
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr><td>
                    <div className="role-detail-loading-wrapper">
                      <Loading type="default" size="3rem" />
                    </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <div className="screen-footer right">
        <div className="btn-wrapper-right screen-footer__btn">
          <ButtonControl
            disabled={saving}
            type={"cancel"}
            onClick={handleOnCancel}
          >
            Cancel
          </ButtonControl>
        </div>
        {canWrite && (
          <div className="btn-wrapper-right screen-footer__btn">
            <ButtonControl
              loading={saving}
              type={"okay"}
              disabled={!surveyItem}
              onClick={handleOnSave}
            >
              Save
            </ButtonControl>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSurveyDetails;
